/**
 * What the AI thinks is going on: threat assessment and the situation label the
 * `EVADE.DAT` weight row is looked up by.
 *
 * The one piece of geometry that matters here is *aspect*. `relativeGeometry`'s
 * `aspectRad` is the angle between the other aircraft's nose and the line back
 * to us — so a small aspect means "he is pointing at me", regardless of where
 * he is, and that (not raw proximity) is what makes something a threat.
 */

import type { DamageState } from "../flight/damage.ts";
import type { SimState } from "../flight/state.ts";
import type { Tunables } from "../flight/tunables.ts";
import { EVADE_SITUATIONS, type EvadeSituation } from "./evade-table.ts";
import type { RelativeGeometry } from "./geometry.ts";

/** Lowest health across the airframe's components — the AI's damage gauge. */
export function worstComponentHealth(damage: DamageState): number {
  return Math.min(damage.engine, damage.wingLeft, damage.wingRight, damage.controls, damage.fuel);
}

export interface ThreatAssessment {
  /** True when the other aircraft has its nose on us. */
  readonly pointedAtUs: boolean;
  /** True when it is behind our own 3/9 line. */
  readonly astern: boolean;
  /** True when it is close enough to shoot. */
  readonly inGunRange: boolean;
  /** True when it is close enough to be worth reacting to at all. */
  readonly nearby: boolean;
}

export function assessThreat(geometry: RelativeGeometry, tunables: Tunables): ThreatAssessment {
  return {
    pointedAtUs: geometry.aspectRad < tunables.aiThreatConeRad,
    astern: geometry.angleOffNoseRad > Math.PI / 2,
    inGunRange: geometry.rangeM < tunables.aiMaxFiringRangeM,
    nearby: geometry.rangeM < tunables.aiThreatRangeM,
  };
}

export interface SituationInput {
  readonly self: SimState;
  readonly geometry: RelativeGeometry | null;
  readonly threat: ThreatAssessment | null;
  /** Set by the encounter on any tick this aircraft took rounds. */
  readonly underFire: boolean;
  readonly tunables: Tunables;
}

/**
 * Ordered worst-first: the label the AI acts on is the most urgent thing true
 * about its situation, not the first one it happens to test.
 */
export function classifySituation(input: SituationInput): EvadeSituation {
  const { self, geometry, threat, tunables } = input;
  // Above the *terrain*, not above datum: the clearance has to mean the same
  // thing over a hill as over a field (`flight/ground.ts`).
  if (self.readout.altitudeAglM < tunables.aiGroundClearanceM) return "lowAltitude";
  if (worstComponentHealth(self.damage) < tunables.aiDamageBreakOffHealth) return "damaged";
  if (input.underFire) return "underFire";
  if (!geometry || !threat) return "clear";
  if (threat.pointedAtUs && threat.inGunRange) return "threatGuns";
  if (threat.pointedAtUs && threat.astern && threat.nearby) return "threatAstern";
  if (threat.pointedAtUs && threat.nearby) return "threatDistant";
  return "clear";
}

/** True for the labels that mean "stop attacking and do something about it". */
export function isDefensive(situation: EvadeSituation): boolean {
  return situation === "threatGuns" || situation === "threatAstern" || situation === "underFire";
}

/** The whole label set is covered by `EVADE.DAT`'s seven rows per block. */
export function situationRowCount(): number {
  return EVADE_SITUATIONS.length;
}
